import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { alternatesFor, canonicalUrlFor } from "@/lib/routeSlugs";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  jsonLd?: Record<string, any> | Record<string, any>[];
  breadcrumbs?: { name: string; url: string }[];
  speakable?: string[];
  articleType?: "Article" | "BlogPosting" | "FAQPage" | "WebPage";
  /** Absolute or root-relative URL of the page-specific share image */
  image?: string;
  /** Alias for `image` */
  ogImage?: string;
  /** Explicit og:type override (defaults derived from articleType) */
  ogType?: string;
  /** Explicit og:locale override, e.g. "pl_PL" */
  locale?: string;
  /** Explicit hreflang map { pl: url, en: url, ... } */
  alternates?: Record<string, string>;
  /** Emit hreflang only for languages present in `alternates` (no auto-filled variants) */
  alternatesOnly?: boolean;
  /** ISO date of first publication (articles) */
  publishedTime?: string;
  /** ISO date of last modification (articles) */
  modifiedTime?: string;
  /** Exclude the page from search engines */
  noindex?: boolean;
  /** Emit no canonical link at all (404 and similar non-pages) */
  noCanonical?: boolean;
}

const BASE = "https://www.stickerstattooo.pl";
const LANGS = ["pl", "en", "de", "fr", "it"] as const;

const OG_LOCALES: Record<string, string> = {
  pl: "pl_PL",
  en: "en_GB",
  de: "de_DE",
  fr: "fr_FR",
  it: "it_IT",
};

const DEFAULT_OG_IMAGE = `${BASE}/og-image.png`;

const ORGANIZATION_SCHEMA = {
  "@type": "Organization",
  "name": "Stickers Tattooo",
  "url": BASE,
  "logo": `${BASE}/assets/logo.png`,
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+48-534-900-620",
    "contactType": "customer service",
    "availableLanguage": ["Polish", "English", "French", "German"],
  },
  "sameAs": [
    "https://www.instagram.com/stickers.tattooo",
    "https://stickers-cut.com",
  ],
};

export default function SEOHead({ title, description, canonical, keywords, jsonLd, breadcrumbs, speakable, articleType, image, ogImage, ogType: ogTypeProp, locale, alternates, alternatesOnly, publishedTime, modifiedTime, noindex, noCanonical }: SEOHeadProps) {
  const location = useLocation();
  const { lang } = useI18n();

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    const ogType = ogTypeProp || (articleType === "Article" || articleType === "BlogPosting" ? "article" : "website");
    setMeta("og:type", ogType, "property");
    setMeta("og:site_name", "Stickers Tattooo", "property");

    // --- Share image (page specific, absolute URL required by crawlers) ---
    const rawImage = image || ogImage;
    const absoluteImage = rawImage
      ? rawImage.startsWith("http")
        ? rawImage
        : `${BASE}${rawImage.startsWith("/") ? "" : "/"}${rawImage}`
      : DEFAULT_OG_IMAGE;
    setMeta("og:image", absoluteImage, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", absoluteImage);

    // --- Locale matching the current page language ---
    setMeta("og:locale", locale || OG_LOCALES[lang] || "pl_PL", "property");
    document.querySelectorAll('meta[property="og:locale:alternate"]').forEach((el) => el.remove());
    for (const l of LANGS) {
      if (l === lang) continue;
      const el = document.createElement("meta");
      el.setAttribute("property", "og:locale:alternate");
      el.setAttribute("content", OG_LOCALES[l]);
      el.setAttribute("data-og-alt", "true");
      document.head.appendChild(el);
    }

    // Article timestamps
    document.querySelectorAll('meta[data-article-time]').forEach((el) => el.remove());
    const setArticleTime = (prop: string, value?: string) => {
      if (!value) return;
      const el = document.createElement("meta");
      el.setAttribute("property", prop);
      el.setAttribute("content", value);
      el.setAttribute("data-article-time", "true");
      document.head.appendChild(el);
    };
    setArticleTime("article:published_time", publishedTime);
    setArticleTime("article:modified_time", modifiedTime);

    // AI crawler directives
    setMeta(
      "robots",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );
    setMeta("googlebot", noindex ? "noindex, nofollow" : "index, follow");

    // --- Canonical ---
    const pathWithoutLang = location.pathname.replace(/^\/(pl|en|de|fr|it)(?=\/|$)/, "").replace(/\/$/, "");
    // Canonical always points at the canonical slug for this language
    const computedCanonical =
      canonical || canonicalUrlFor(location.pathname, lang) || `${BASE}/${lang}${pathWithoutLang || "/"}`;

    const existingCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (noCanonical) {
      existingCanonical?.remove();
      document.querySelector('meta[property="og:url"]')?.remove();
    } else {
      setMeta("og:url", computedCanonical, "property");
      const canonicalLink = existingCanonical || document.createElement("link");
      canonicalLink.rel = "canonical";
      canonicalLink.href = computedCanonical;
      if (!existingCanonical) document.head.appendChild(canonicalLink);
    }

    // --- Hreflang tags ---
    // Remove old hreflang links
    document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());

    const routeAlternates = alternatesFor(location.pathname);

    for (const l of LANGS) {
      if (noCanonical) break;
      // alternatesOnly: declare hreflang only for the languages explicitly provided
      if (alternatesOnly && !alternates?.[l]) continue;
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = l;
      link.href = alternates?.[l] || routeAlternates?.[l] || `${BASE}/${l}${pathWithoutLang || "/"}`;
      link.setAttribute("data-hreflang", "true");
      document.head.appendChild(link);
    }
    // x-default
    if (!noCanonical) {
      const xDefault = document.createElement("link");
      xDefault.rel = "alternate";
      xDefault.hreflang = "x-default";
      xDefault.href = alternates?.["x-default"] || routeAlternates?.["x-default"] || `${BASE}/pl${pathWithoutLang || "/"}`;
      xDefault.setAttribute("data-hreflang", "true");
      document.head.appendChild(xDefault);
    }

    // Build combined JSON-LD graph
    const graph: Record<string, any>[] = [];

    // WebSite schema
    graph.push({
      "@type": "WebSite",
      "name": "Stickers Tattooo",
      "url": BASE,
      "description": "Drukarnia naklejek premium – naklejki foliowe, holograficzne, die-cut na zamówienie.",
      "inLanguage": ["pl", "en", "fr", "de"],
      "publisher": ORGANIZATION_SCHEMA,
    });

    // Breadcrumbs
    if (breadcrumbs && breadcrumbs.length > 0) {
      graph.push({
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": b.name,
          "item": b.url,
        })),
      });
    }

    // Page-specific JSON-LD
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      for (const item of items) {
        if (item["@graph"]) {
          graph.push(...item["@graph"]);
        } else if (item["@context"]) {
          const { "@context": _, ...rest } = item;
          if (speakable && (rest["@type"] === "Article" || rest["@type"] === "BlogPosting" || rest["@type"] === "WebPage")) {
            rest.speakable = {
              "@type": "SpeakableSpecification",
              "cssSelector": speakable,
            };
          }
          graph.push(rest);
        } else {
          graph.push(item);
        }
      }
    }

    // Add speakable to any Article/BlogPosting in graph
    if (speakable) {
      for (const node of graph) {
        if (["Article", "BlogPosting", "WebPage"].includes(node["@type"]) && !node.speakable) {
          node.speakable = {
            "@type": "SpeakableSpecification",
            "cssSelector": speakable,
          };
        }
      }
    }

    const existingScript = document.querySelector('script[data-seo-jsonld]');
    if (existingScript) existingScript.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo-jsonld", "true");
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      script.remove();
      document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());
      document.querySelectorAll('meta[data-og-alt]').forEach(el => el.remove());
      document.querySelectorAll('meta[data-article-time]').forEach(el => el.remove());
    };
  }, [title, description, canonical, keywords, jsonLd, breadcrumbs, speakable, articleType, image, ogImage, ogTypeProp, locale, alternates, alternatesOnly, publishedTime, modifiedTime, noindex, noCanonical, location.pathname, lang]);

  return null;
}
